import { DFValues } from '@vmware-react/vrlcm';
import {
  LcmNtp,
  LoadBalancerConfiguration,
  LogInSightAgentConfiguration,
  NotificationTriggers,
  OutboundNotification,
  RequestId,
  RequestName,
  RequestState,
  SSHDetails,
  SystemUpgradeDetails,
  Telemetry
} from 'vrlcm-gui-types';
import { Http } from 'vrlcm-gui-utils';
import { SettingsApi } from './Settings';
import { LcopsApiUri } from './uri';
import { LockerApiUri } from '../locker';
import { RequestApi } from '../request';
import { ServersAndAccountsApi } from './ServersAndAccounts';
export class SystemAdministrationApi {
  public static addLcmNtpServers = (servers: string): Promise<LcmNtp> => {
    const body = {
      syncWithHost: false,
      ntpServerEnabled: true,
      ntpServers: servers
    };
    return Http.post(LcopsApiUri.Settings.LCM_NTP_SERVERS_URL, body).then(
      res => res.data
    );
  };

  public static lcmAgentConfiguration = (
    configuration: LogInSightAgentConfiguration
  ): Promise<LogInSightAgentConfiguration> => {
    return Http.post(
      LcopsApiUri.Settings.LCM_LOG_AGENT_CONFIGURATION_URL,
      configuration
    ).then(res => res.data);
  };

  public static setLcmNtpHostTime = (): Promise<LcmNtp> => {
    const body = {
      syncWithHost: true,
      ntpServerEnabled: false,
      ntpServers: ''
    };
    return Http.post(LcopsApiUri.Settings.LCM_NTP_SERVERS_URL, body).then(
      res => res.data
    );
  };

  public static getLcmNtpDetails = (): Promise<LcmNtp> => {
    return Http.get(LcopsApiUri.Settings.LCM_NTP_SERVERS_URL).then(
      res => res.data
    );
  };

  public static getEulaStatus = (): Promise<Telemetry> => {
    return SettingsApi.getTelemetryInformation();
  };

  public static updateEulaStatus = (
    telemetryEnabled: boolean
  ): Promise<Telemetry> => {
    const body = { telemetryEnabled };
    return Http.post(LcopsApiUri.Settings.LCM_TELEMETRY_URL, body).then(
      res => res.data
    );
  };

  public static updateSshStatus = (
    sshStatus: DFValues
  ): Promise<SSHDetails> => {
    const sshStatusValue = sshStatus.sshStatus ? 'true' : 'false';
    const body = { sshStatus: sshStatusValue };
    return Http.post(LcopsApiUri.Settings.GET_SSH_INFORMATION_URL, body).then(
      res => res.data
    );
  };

  public static submitSelfPatchRequest = (
    patchId: string
  ): Promise<RequestId> => {
    return Http.post(
      LcopsApiUri.Product.SUBMIT_SELF_PATCH_REQUEST_URL(patchId)
    ).then(res => res.data);
  };
  public static applyPSPackRequest = async (
    psPackID: string
  ): Promise<RequestId> => {
    const response = await Http.post(
      LcopsApiUri.Product.APPLY_PSPACK_REQUEST_URL(psPackID)
    );
    return response.data;
  };
  public static getLcmCertificate = async (): Promise<any> => {
    const response = await Http.get(
      LcopsApiUri.Product.GET_LCM_CERTIFICATE_URL
    );
    return response.data;
  };
  public static replaceCertificatePreCheck = async (
    vmid: string
  ): Promise<RequestId> => {
    let body;

    body = {
      certificateVmId: vmid,
      components: []
    };
    const response = await Http.post(
      LcopsApiUri.Product.LCM_REPLACE_CERTIFICATE_PRE_CHECK_URL,
      body
    );
    return response.data;
  };
  public static systemUpgradePreCheck = async (
    systemUpgradeDetails: SystemUpgradeDetails
  ): Promise<RequestId> => {
    const payload = Object.assign(systemUpgradeDetails, {
      actionType: 'upgrade'
    });
    const response = await Http.post(
      LcopsApiUri.Product.LCM_SYSTEM_UPGRADE_PRE_CHECK_URL,
      payload
    );
    return response.data;
  };
  public static replaceCertificate = async (
    vmid: string
  ): Promise<RequestId> => {
    let body;

    body = {
      certificateVmId: vmid,
      components: []
    };
    const response = await Http.post(
      LcopsApiUri.Product.LCM_REPLACE_CERTIFICATE_URL,
      body
    );
    return response.data;
  };
  public static isProductSupportPackRequestInProgress = async (): Promise<
    boolean
  > => {
    const requests = await RequestApi.getRequestWithQueryOptions({
      filter: {
        requestTypes: [RequestName.ProductSupportPack],
        requestStates: [RequestState.INPROGRESS]
      },
      sortByDirection: 'DESC',
      sortBy: 'lastUpdatedOn',
      limit: 1
    });
    return requests.length > 0;
  };

  public static getNotificationTriggers = (): Promise<NotificationTriggers> => {
    return Http.get(LockerApiUri.Policies.GET_NOTIFICATION_TRIGGERS_URL).then(
      res => res.data
    );
  };

  public static getOutboundNotifications = (): Promise<
    OutboundNotification[]
  > => {
    return Http.get(LcopsApiUri.SettingsV2.OUTBOUND_NOTIFICATIONS_URL).then(
      res => res.data
    );
  };

  public static async addOutboundNotification(
    outboundNotification: OutboundNotification
  ) {
    const response = await Http.post<OutboundNotification>(
      LcopsApiUri.SettingsV2.OUTBOUND_NOTIFICATIONS_URL,
      outboundNotification
    );
    return response.data;
  }

  public static async editOutboundNotification(
    outboundNotification: OutboundNotification
  ) {
    const response = await Http.put<OutboundNotification>(
      LcopsApiUri.SettingsV2.OUTBOUND_NOTIFICATIONS_URL,
      outboundNotification
    );
    return response.data;
  }

  public static async testWebhook(type: string, webhook: string) {
    const response = await Http.get(
      LcopsApiUri.VALIDATE_WEBHOOK_URL(type, webhook)
    );
    return response.data;
  }

  public static async updateOutboundNotification(
    outboundNotification: OutboundNotification,
    editMode: boolean
  ) {
    if (editMode) {
      return SystemAdministrationApi.editOutboundNotification(
        outboundNotification
      );
    } else {
      return SystemAdministrationApi.addOutboundNotification(
        outboundNotification
      );
    }
  }

  public static async outboundNotifications() {
    return await Promise.all([
      SystemAdministrationApi.getNotificationTriggers(),
      SystemAdministrationApi.getOutboundNotifications(),
      ServersAndAccountsApi.getSmtpConfigurations()
    ]);
  }

  public static async saveControllerDetails(
    loadBalancer: LoadBalancerConfiguration
  ) {
    const response = await Http.post<LoadBalancerConfiguration>(
      LcopsApiUri.Controllers.SAVE_CONTROLLER_DETAILS,
      loadBalancer
    );
    return response.data;
  }

  public static async validateController(
    loadBalancer: LoadBalancerConfiguration
  ) {
    const body = {
      controllerFqdn: loadBalancer.controllerFqdn,
      controllerIP: loadBalancer.controllerIP,
      controllerMeta: loadBalancer.controllerMeta,
      loadBalancerFQDN: loadBalancer.loadBalancerFQDN,
      loadBalancerIP: loadBalancer.loadBalancerIP,
      loadBalancerMeta: loadBalancer.loadBalancerMeta,
      passwordReference: loadBalancer.passwordReference,
      type: loadBalancer.type,
      userName: loadBalancer.userName
    };
    const url = LcopsApiUri.Controllers.VALIDATE_CONTROLLER;
    return Http.post(url, body).then(res => res.data);
  }

  public static async getAllCloudAccounts(
    loadBalancer: LoadBalancerConfiguration
  ) {
    const body = {
      controllerFqdn: loadBalancer.controllerFqdn,
      controllerIP: loadBalancer.controllerIP,
      controllerMeta: loadBalancer.controllerMeta,
      loadBalancerFQDN: loadBalancer.loadBalancerFQDN,
      loadBalancerIP: loadBalancer.loadBalancerIP,
      loadBalancerMeta: loadBalancer.loadBalancerMeta,
      passwordReference: loadBalancer.passwordReference,
      type: loadBalancer.type,
      userName: loadBalancer.userName
    };
    const url = LcopsApiUri.Controllers.GET_ALL_CONTROLLER_CLOUDS;
    return Http.post(url, body).then(res => res.data);
  }

  public static async getAllControllerDetails(
    type: string,
    isAvailable: boolean
  ) {
    const url = LcopsApiUri.Controllers.GET_ALL_CONTROLLER_DETAILS;
    const searchParams = new URLSearchParams();
    searchParams.append('type', type);
    return Http.get(
      `${url}?${searchParams.toString()}&isAvailable=${isAvailable}`
    ).then(res => res.data);
  }

  public static async deleteLoadBalancer(
    controllerID: string,
    loadBalancerFQDN: string
  ) {
    const url = LcopsApiUri.Controllers.DELETE_LB;
    const searchParams = new URLSearchParams();
    searchParams.append('controllerID', controllerID);
    searchParams.append('loadBalancerFQDN', loadBalancerFQDN);
    return Http.delete(`${url}?${searchParams.toString()}`).then(
      res => res.data
    );
  }

  public static async saveMinionMasters(minionMasters: string[]) {
    const url = LcopsApiUri.Settings.MINION_MASTERS_URL;
    const body = {
      master: minionMasters
    };
    return Http.post(url, body).then(res => res.data.requestId);
  }

  public static async getMinionMasters() {
    const url = LcopsApiUri.Settings.MINION_MASTERS_URL;
    return Http.get(url).then(res => res.data);
  }

  public static async deleteMinionMaster(minionMasters: string[]) {
    const url = LcopsApiUri.Settings.MINION_MASTERS_URL;
    const body = {
      master: minionMasters
    };
    return Http.post(url, body).then(res => res.data.requestId);
  }

  public static async saveMinionGrains(minionGrains: any) {
    const url = LcopsApiUri.Settings.MINION_GRAINS_URL;
    const body = { grains: minionGrains };
    return Http.post(url, body).then(res => res.data.requestId);
  }

  public static async getMinionGrains() {
    const url = LcopsApiUri.Settings.MINION_GRAINS_URL;
    return Http.get(url).then(res => res.data);
  }

  public static async editMinionGrains(minionGrains: any) {
    const url = LcopsApiUri.Settings.MINION_GRAINS_URL;
    const body = { grains: minionGrains };
    return Http.post(url, body).then(res => res.data.requestId);
  }

  public static async deleteMinionGrains(minionGrains: any) {
    const url = LcopsApiUri.Settings.MINION_GRAINS_URL;
    const body = { grains: minionGrains };
    return Http.post(url, body).then(res => res.data.requestId);
  }

  public static async isMinionInstalled() {
    const url = LcopsApiUri.Settings.CHECK_MINION_INSTALLED;
    return Http.get(url).then(res => res.data);
  }

  public static async installMinion(version: string) {
    const url = LcopsApiUri.Settings.INSTALL_MINION;
    const searchParams = new URLSearchParams();
    searchParams.append('version', version);
    return Http.post(`${url}?${searchParams.toString()}`).then(
      res => res.data.requestId
    );
  }

  public static async upgradeMinion(version: string) {
    const url = LcopsApiUri.Settings.UPGRADE_MINION;
    const searchParams = new URLSearchParams();
    searchParams.append('version', version);
    return Http.post(`${url}?${searchParams.toString()}`).then(
      res => res.data.requestId
    );
  }
}
